import type { ChangeEvent, KeyboardEvent as ReactKeyboardEvent } from 'react';
import type { LayoutResult } from './layout-treemap';
import {
  FIT_VIEW_DURATION,
  FIT_VIEW_PADDING,
  MAX_ZOOM,
  MIN_ZOOM,
} from './treemap-constants';
import { getTreemapKeyCommand, runTreemapKeyCommand } from './treemap-keyboard';
import type { ParsedTreemap } from './treemap-types';
import {
  getDrillPathTargetId,
  getTreemapSectionDrillPath,
  isTreemapViewportRect,
  type TreemapViewportRect,
} from './treemap-view-model';
import type {
  SetDrillPathIds,
  SetHoveredNodeId,
  SetZoomLevel,
  TreemapReactFlowInstance,
} from './treemap-interaction-types';

export const ZOOM_STEP_DURATION = 180;
const ZOOM_SLIDER_DURATION = 120;

export function fitTreemapRect(input: {
  reactFlowInstance: TreemapReactFlowInstance | null;
  rect: TreemapViewportRect | undefined;
}): void {
  if (!input.reactFlowInstance || !isTreemapViewportRect(input.rect)) {
    return;
  }

  input.reactFlowInstance.fitBounds(input.rect, {
    duration: FIT_VIEW_DURATION,
    padding: FIT_VIEW_PADDING,
  });
}

export function fitTreemapOverview(
  reactFlowInstance: TreemapReactFlowInstance | null,
): void {
  reactFlowInstance?.fitView({ duration: FIT_VIEW_DURATION, padding: FIT_VIEW_PADDING });
}

export function fitTreemapDrillPath(input: {
  reactFlowInstance: TreemapReactFlowInstance | null;
  rectById: Map<string, TreemapViewportRect>;
  pathIds: string[];
}): void {
  const targetId = getDrillPathTargetId(input.pathIds);
  if (!targetId) {
    fitTreemapOverview(input.reactFlowInstance);
    return;
  }

  fitTreemapRect({
    reactFlowInstance: input.reactFlowInstance,
    rect: input.rectById.get(targetId),
  });
}

export function drillIntoTreemapNode(input: {
  parsed: ParsedTreemap | null;
  parentMap: Map<string, string>;
  rectById: Map<string, TreemapViewportRect>;
  reactFlowInstance: TreemapReactFlowInstance | null;
  setDrillPathIds: SetDrillPathIds;
  nodeId: string;
}): void {
  const chain = getTreemapSectionDrillPath({
    parsed: input.parsed,
    parentMap: input.parentMap,
    nodeId: input.nodeId,
  });
  if (!chain) {
    return;
  }

  input.setDrillPathIds(chain);
  fitTreemapRect({
    reactFlowInstance: input.reactFlowInstance,
    rect: input.rectById.get(input.nodeId),
  });
}

export function drillToTreemapDepth(input: {
  drillPathIds: string[];
  index: number;
  reactFlowInstance: TreemapReactFlowInstance | null;
  rectById: Map<string, TreemapViewportRect>;
  resetView: () => void;
  setDrillPathIds: SetDrillPathIds;
}): void {
  if (input.index < 0) {
    input.resetView();
    return;
  }

  const targetId = input.drillPathIds[input.index];
  input.setDrillPathIds((previous) => previous.slice(0, input.index + 1));
  if (targetId) {
    fitTreemapRect({
      reactFlowInstance: input.reactFlowInstance,
      rect: input.rectById.get(targetId),
    });
  }
}

export function ascendTreemapDrill(input: {
  previous: string[];
  reactFlowInstance: TreemapReactFlowInstance | null;
  rectById: Map<string, TreemapViewportRect>;
}): string[] {
  if (input.previous.length === 0) {
    return input.previous;
  }

  const next = input.previous.slice(0, -1);
  fitTreemapDrillPath({
    reactFlowInstance: input.reactFlowInstance,
    rectById: input.rectById,
    pathIds: next,
  });

  return next;
}

export function updateZoomFromSlider(input: {
  event: ChangeEvent<HTMLInputElement>;
  reactFlowInstance: TreemapReactFlowInstance | null;
  setZoomLevel: SetZoomLevel;
}): void {
  if (!input.reactFlowInstance) {
    return;
  }

  const parsedZoom = Number(input.event.target.value);
  if (!Number.isFinite(parsedZoom)) {
    return;
  }

  const targetZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, parsedZoom));
  input.setZoomLevel(targetZoom);
  input.reactFlowInstance.zoomTo(targetZoom, { duration: ZOOM_SLIDER_DURATION });
}

export function setHoveredNodeIfPresent(input: {
  nodeId: string | null;
  setHoveredNodeId: SetHoveredNodeId;
}): void {
  if (input.nodeId) {
    input.setHoveredNodeId(input.nodeId);
  }
}

export function runTreemapKeyDown(input: {
  event: ReactKeyboardEvent<HTMLDivElement>;
  layout: LayoutResult | null;
  hoveredNodeId: string | null;
  handleZoomIn: () => void;
  handleZoomOut: () => void;
  ascendDrill: () => void;
  focusParent: () => void;
  focusFirstChild: () => void;
  focusPreviousSibling: () => void;
  focusNextSibling: () => void;
  drillIntoNode: (nodeId: string) => void;
}): void {
  const command = input.layout ? getTreemapKeyCommand(input.event.key) : null;
  if (!command) {
    return;
  }

  input.event.preventDefault();
  runTreemapKeyCommand({
    command,
    hoveredNodeId: input.hoveredNodeId,
    zoomIn: input.handleZoomIn,
    zoomOut: input.handleZoomOut,
    ascendDrill: input.ascendDrill,
    focusParent: input.focusParent,
    focusFirstChild: input.focusFirstChild,
    focusPreviousSibling: input.focusPreviousSibling,
    focusNextSibling: input.focusNextSibling,
    drillIntoNode: input.drillIntoNode,
  });
}
